import { useMemo } from 'react';
import { Check, Calendar, Phone, Users, Bell, MoreHorizontal } from 'lucide-react';
import type { CalendarItem, CalendarEventType } from '../../types/calendar';
import {
  getMonthViewDays,
  WEEKDAY_LABELS,
  isToday,
  getItemsForDay,
  CALENDAR_EVENT_TYPE_COLORS,
} from '../../types/calendar';

interface CalendarMonthViewProps {
  currentDate: Date;
  items: CalendarItem[];
  onDayClick: (date: Date) => void;
  onItemClick: (item: CalendarItem) => void;
  focusedEventId?: string | null;
}

const EVENT_TYPE_ICONS: Record<CalendarEventType, typeof Calendar> = {
  event: Calendar,
  call: Phone,
  meeting: Users,
  reminder: Bell,
  custom: MoreHorizontal,
};

export function CalendarMonthView({
  currentDate,
  items,
  onDayClick,
  onItemClick,
  focusedEventId,
}: CalendarMonthViewProps) {
  const days = useMemo(() => getMonthViewDays(currentDate, true), [currentDate]);
  const currentMonth = currentDate.getMonth();

  const weekDays = useMemo(() => {
    // Start on Monday
    return [1, 2, 3, 4, 5, 6, 0].map((i) => WEEKDAY_LABELS[i]);
  }, []);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
      {/* Header with weekday names */}
      <div className="grid grid-cols-7 border-b border-gray-100 dark:border-slate-700">
        {weekDays.map((day) => (
          <div
            key={day}
            className="py-3 text-center text-sm font-medium text-[#6B6B6B] dark:text-slate-400"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {days.map((day, index) => {
          const isCurrentMonth = day.getMonth() === currentMonth;
          const dayItems = getItemsForDay(items, day);
          const hasMore = dayItems.length > 3;
          const displayItems = dayItems.slice(0, 3);

          return (
            <div
              key={index}
              onClick={() => onDayClick(day)}
              className={`
                min-h-[100px] p-1 border-b border-r border-gray-100 dark:border-slate-700 cursor-pointer
                hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors
                ${!isCurrentMonth ? 'bg-gray-50/50 dark:bg-slate-900/50' : ''}
              `}
            >
              {/* Day number */}
              <div className="flex justify-end mb-1">
                <span
                  className={`
                    w-7 h-7 flex items-center justify-center rounded-full text-sm
                    ${isToday(day) ? 'bg-primary text-white font-semibold' : ''}
                    ${!isCurrentMonth ? 'text-gray-400 dark:text-slate-500' : 'text-[#1A1A1A] dark:text-white'}
                  `}
                >
                  {day.getDate()}
                </span>
              </div>

              {/* Items */}
              <div className="space-y-0.5">
                {displayItems.map((item) => (
                  <CalendarItemPill
                    key={item.id}
                    item={item}
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemClick(item);
                    }}
                    isFocused={focusedEventId === item.id || focusedEventId === item.originalEventId}
                  />
                ))}
                {hasMore && (
                  <div className="text-xs text-[#6B6B6B] dark:text-slate-400 pl-1">
                    +{dayItems.length - 3} weitere
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface CalendarItemPillProps {
  item: CalendarItem;
  onClick: (e: React.MouseEvent) => void;
  isFocused?: boolean;
}

function CalendarItemPill({ item, onClick, isFocused }: CalendarItemPillProps) {
  if (item.source === 'todo') {
    return (
      <button
        onClick={onClick}
        className={`
          w-full text-left px-1.5 py-0.5 rounded text-xs truncate
          flex items-center gap-1 transition-all
          ${isFocused
            ? 'ring-2 ring-primary ring-offset-1 shadow-md'
            : ''
          }
          ${item.todoCompleted
            ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 line-through'
            : 'bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300'
          }
        `}
      >
        <Check className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{item.title}</span>
      </button>
    );
  }

  const colors = item.eventType
    ? CALENDAR_EVENT_TYPE_COLORS[item.eventType]
    : CALENDAR_EVENT_TYPE_COLORS.event;
  const Icon = item.eventType ? EVENT_TYPE_ICONS[item.eventType] : Calendar;

  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-1.5 py-0.5 rounded text-xs truncate
        flex items-center gap-1 transition-all
        ${isFocused
          ? 'ring-2 ring-primary ring-offset-1 shadow-md'
          : ''
        }
        ${colors.bg} ${colors.text}
      `}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{item.title}</span>
    </button>
  );
}
